/**
 * Internal branch-order slip printer.
 *
 * Reuses the SAME hidden-iframe `window.print()` channel the POS already
 * uses (see usePosPrinting.openOrderInPrintDialog / printBridge.printViaAirPrint)
 * — NOT a new printer implementation. This slip is strictly internal and
 * NON-TAX: no VAT, no ZATCA QR, no sales totals. It simply documents a
 * branch-to-branch stock order so the receiving branch can pick/dispatch.
 */

const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

function fmtDate(dt) {
  if (!dt) return '';
  try { return new Date(dt).toLocaleString('en-GB'); } catch { return String(dt); }
}

/**
 * @param {object} slip
 * @param {string} [slip.company]       company / brand (optional header)
 * @param {string} [slip.requestNumber] request / order number
 * @param {string} slip.fromBranch      source branch (fulfils / sends)
 * @param {string} slip.toBranch        destination branch (requester)
 * @param {string|Date} [slip.dateTime] requested date/time
 * @param {Array}  slip.items           [{ name, qty, unit }]
 * @param {string} [slip.notes]         reason / notes
 * @param {string} [slip.status]        current status
 */
export function printBranchOrderSlip(slip = {}) {
  const items = (slip.items || []).map((it) => {
    const qty = it.qty != null ? it.qty : (it.quantity != null ? it.quantity : '');
    const unit = it.unit || '';
    const name = it.name || it.item_name || it.item || '';
    return `<div style="display:flex;justify-content:space-between;gap:8px"><span>${esc(name)}</span><span>${esc(qty)} ${esc(unit)}</span></div>`;
  }).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8"/><style>
    @page { size: 80mm auto; margin: 2mm; }
    body { font-family: monospace; width: 76mm; padding: 4mm; font-size: 12px; color:#000; }
    .c { text-align:center; }
    .b { font-weight:bold; }
    .big { font-size:15px; }
    .muted { color:#555; font-size:11px; }
    hr { border:none; border-top:1px dashed #999; margin:5px 0; }
    .row { display:flex; justify-content:space-between; gap:8px; margin:1px 0; }
  </style></head><body>
    ${slip.company ? `<div class="c b big">${esc(slip.company)}</div>` : ''}
    <div class="c b">BRANCH STOCK ORDER</div>
    <div class="c muted">Internal transfer slip — not a tax invoice</div>
    <hr/>
    ${slip.requestNumber ? `<div class="row"><span>Order #</span><span class="b">${esc(slip.requestNumber)}</span></div>` : ''}
    ${slip.dateTime ? `<div class="row"><span>Date</span><span>${esc(fmtDate(slip.dateTime))}</span></div>` : ''}
    ${slip.status ? `<div class="row"><span>Status</span><span>${esc(String(slip.status).toUpperCase())}</span></div>` : ''}
    <hr/>
    <div class="row"><span>From</span><span class="b">${esc(slip.fromBranch || '')}</span></div>
    <div class="c muted">↓</div>
    <div class="row"><span>To</span><span class="b">${esc(slip.toBranch || '')}</span></div>
    <hr/>
    <div class="b">Items</div>
    ${items || '<div class="muted">No items</div>'}
    ${slip.notes ? `<hr/><div class="muted">Notes: ${esc(slip.notes)}</div>` : ''}
    <hr/>
    <div class="c muted">Please pick &amp; dispatch the above items.</div>
    <div style="height:14px"></div>
    <script>window.onload=function(){setTimeout(function(){window.print();},200)}</script>
  </body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
  setTimeout(() => { try { iframe.remove(); } catch { /* */ } }, 30000);
}

export default printBranchOrderSlip;
